"use client";
import { useEffect } from "react";
import { useAppDispatch } from "@client/store/store.hooks";
import { GuestStorage, MediaStorage } from "@shared/utils";
import {
  setUsername,
  setGuestEmail,
  setGuestMode,
  setGuestData,
  setIsJoining,
  setJoinRejection,
  setMediaDefaults,
  setMeetingDetails,
  PreJoinMeetingDetails
} from "@client/store/slices/preJoin.slice";
import { STORAGE_KEYS } from "@shared/constants/constant";

// Storage key for identifying host rooms
const STORAGE_KEY_HOST_ROOMS = STORAGE_KEYS.CLIENT.HOST_ROOMS;

interface UseMeetingInitializationProps {
  roomName: string;
  userDisplayName?: string;
  meetingDetails?: PreJoinMeetingDetails | null;
  setIsHost: (isHost: boolean) => void;
}

export const useMeetingInitialization = ({
  roomName,
  userDisplayName,
  meetingDetails,
  setIsHost
}: UseMeetingInitializationProps) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Reset join state on mount to prevent stale state from navigation
    dispatch(setIsJoining(false));
    dispatch(setJoinRejection({ isRejected: false, message: null }));

    // Check host status
    const hostRooms = JSON.parse(localStorage.getItem(STORAGE_KEY_HOST_ROOMS) || "[]");
    setIsHost(hostRooms.includes(roomName));

    // Initialize authenticated user name or guest mode
    if (userDisplayName) {
      dispatch(setUsername(userDisplayName));
      dispatch(setGuestMode(false));
      dispatch(setGuestEmail(""));
    } else {
      // Guest mode logic
      dispatch(setGuestMode(true));
      GuestStorage.cleanupExpired();
      const existingGuest = GuestStorage.getDataForRoom(roomName);
      if (existingGuest) {
        dispatch(setGuestData(existingGuest));
        dispatch(setGuestEmail(existingGuest.guestEmail || ""));
      } else {
        dispatch(setGuestData(null));
        dispatch(setGuestEmail(""));
      }

      // Check for stored rejection
      // Check for stored rejection
      const storedRejection = GuestStorage.getRejection(roomName);
      if (storedRejection) {
        dispatch(setJoinRejection({ isRejected: true, message: storedRejection.message }));
      }
    }

    // Load media preferences from local storage
    const prefs = MediaStorage.getPreferences();
    dispatch(
      setMediaDefaults({
        audioEnabled: prefs.micEnabled,
        videoEnabled: prefs.cameraEnabled
      })
    );

    // Store meeting details in redux
    dispatch(setMeetingDetails(meetingDetails || null));
  }, [roomName, userDisplayName, meetingDetails, dispatch, setIsHost]);
};
